import { ElementRef, EventEmitter, Injectable } from '@angular/core';
import { MapInteractionDraw } from '@app/utilities/map/interactions/draw';
import { geojson } from '@lib/geojson';
import Map from 'ol/Map';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { DrawingManagerOption } from './location.component';
import { MapCustomButtons } from '@app/utilities/map/interactions/buttons';
import { BehaviorSubject, Subject, Subscription, combineLatest } from 'rxjs';
import { Marker, MarkerPoint } from '@app/utilities/map/features/marker';
import { TooltipManager } from '@app/utilities/map/features/tooltip';
import { PolygonManager } from '@app/utilities/map/features/polygon';
import { Device, Vehicle, VehicleLocationQuery } from '@models';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { WebClient } from '@injectables';
import { View } from 'ol';
import { PolylineManager, PolylineType } from '@app/utilities/map/features/polyline';
import { createMapSatelliteLayer } from '@app/utilities/map/ol/satellite-layer';
import Zoom from 'ol/control/Zoom';
import { DialogAddSiteComponent } from '@app/old-ui/dialog/dialog-add-site/dialog-add-site.component';
import { Color } from '@lib/color';
import { map } from 'rxjs/operators';

type TooltipType = 'device' | 'vehicle' | 'muckpile';

@Injectable({
    providedIn: 'root',
})

export class LocationMapService {
    public map: Map | null = null;
    public mapButtons: MapCustomButtons;
    public markerManager: Marker;
    public tooltipManager: TooltipManager;
    public polygonManager: PolygonManager;
    public polylineManager: PolylineManager;
    private readonly addSiteDialog = DialogAddSiteComponent;
    public readonly vehicleListDrawerToggle$ = new Subject<void>();
    public readonly geofenceRecordDrawerToggle$ = new Subject<void>();
    private readonly tooltipVisibility: Record<TooltipType, boolean> = {
        device: false,
        vehicle: false,
        muckpile: false,
    };
    public readonly deviceClient = this.webClient.model.device;
    public readonly trigger$ = this.fragQuery.trigger$;
    private mapInteractionDraw: MapInteractionDraw;
    public vehicleProperties$ = new BehaviorSubject<[Vehicle, Color, boolean][]>([]);
        
    private subscriptions: Subscription = new Subscription();
    
    constructor(
        private vehicleQuery: VehicleQueryService,
        private fragQuery: FragQueryService,
        private readonly dialog: DialogService,
        private readonly webClient: WebClient,
    ) {
    this.mapInteractionDraw = new MapInteractionDraw();
    }

    public initializeMap(center: geojson.Coord, mapElement: ElementRef) {
        this.map = this.initializeBaseSatelliteLayer(mapElement.nativeElement, center, 16);
        this.tooltipManager = new TooltipManager(this.map);
        this.mapButtons = new MapCustomButtons(mapElement.nativeElement);
        this.markerManager = new Marker(this.map, this.tooltipManager);
        this.polygonManager = new PolygonManager(this.map, this.tooltipManager);
        this.polylineManager = new PolylineManager(this.map, this.tooltipManager);
        this.addControlButtons();
        this.startLiveVehicleTracing();

        this.subscriptions.add(this.vehicleQuery.devices$.subscribe(devices => {
            if (devices && Array.isArray(devices) && devices.length > 0) {
                this.markerManager.clearByCategory('device');
                devices.forEach((device) => {
                    if (device?.geometry) {
                        this.reCenterMap(JSON.parse(device.geometry)?.coordinates, 16);
                        this.addMarker({
                            coordinates: JSON.parse(device.geometry)?.coordinates,
                            coordinateSystem: 'utm',
                            title: `<strong>Device: </strong> ${device.name}`,
                            device,
                            iconPath: '../../../assets/mat-icons/camera.svg',
                            draggable: true,
                        })
                    }
                });

                this.refreshVisibleTooltips('device');
            }
        }));
        this.fragQuery.trigger$.next()

        this.fragQuery.muckpiles$.subscribe((muckpiles) => {
            if (!this.map || !this.polygonManager) {
                return;
            }

            this.polygonManager.clearAll();
            (muckpiles ?? []).filter(muckpile => muckpile.isActive).forEach(muckpile => {
                    this.addPolygon({
                        coordinates: muckpile.geometry.coordinates,
                        color: muckpile.color,
                        title: muckpile.name
                    })
                });

            this.refreshVisibleTooltips('muckpile');
        })
    }

    public initializeBaseSatelliteLayer(
        mapElement: HTMLElement,
        center: geojson.Coord,
        zoom: number
    ) {
        this.map = new Map({
            target: mapElement,
            layers: [createMapSatelliteLayer()],
            view: new View({
                center: center,
                zoom: zoom
            }),
            controls: [new Zoom()]
        });
        return this.map;
    }

    public reCenterMap(center: geojson.Coord, zoom?: number) {
        if (this.map) {
            const view = this.map.getView();
            view.setCenter(center);
            view.setZoom(zoom);
        }
    }

    public addTracePaths(tracePaths: PolylineType[]) {
        if (!this.polylineManager) return;
        this.polylineManager.clear();
        tracePaths.forEach((tracePath) => {
            this.polylineManager.add({
                coordinates: tracePath.coordinates,
                color: tracePath.color.toString(),
                content: 'Trace Path'
            })
        });
    }

    private addControlButtons() {
        this.mapButtons.add({
            text: "Menu",
            icon: '../../../assets/mat-icons/menu.png',
            position: 'top-left',
            click: () => { this.vehicleListDrawerToggle$.next() }
        });
        this.mapButtons.add({
            text: "Geofence Records",
            icon: '../../../assets/mat-icons-v2/geofence-record.svg',
            position: 'top-left',
            click: () => { this.geofenceRecordDrawerToggle$.next() }
        });
        this.mapButtons.add({
            text: "Muckpile Actions",
            icon: '../../../assets/mat-icons-v2/muckpile-black.svg',
            position: 'top-left',
            click: () => { },
            subButtons: [
                {
                    text: "Add/Import Muckpile",
                    icon: '../../../assets/mat-icons-v2/muckpile-add.svg',
                    click: () => { this.addMuckpile() },
                },
                {
                    text: "Activate/Inactivate Muckpile",
                    icon: '../../../assets/mat-icons-v2/muckpile-inactive.svg',
                    click: () => { this.dialog.openChangeMuckpileStatusDialog(); },
                },
                {
                    text: "Delete Muckpile",
                    icon: '../../../assets/mat-icons-v2/muckpile-delete.svg',
                    click: () => { this.dialog.openDeleteMuckpileDialog() },
                }
            ]
        });
        this.mapButtons.add({
            text: "Toggle Device Tooltip",
            icon: '../../../assets/mat-icons/camera-black.svg',
            position: 'top-right',
            click: () => {
                this.toggleDeviceTooltips();
            }
        });
        this.mapButtons.add({
            text: "Toggle Vehicle Tooltip",
            icon: '../../../assets/mat-icons/vehicle-black.svg',
            position: 'top-right',
            click: () => {
                this.toggleVehicleTooltips();
            }
        });
        this.mapButtons.add({
            text: "Toggle Muckpile Tooltip",
            icon: '../../../assets/mat-icons-v2/muckpile-black.svg',
            position: 'top-right',
            click: () => {
                this.toggleMuckpileTooltips();
            }
        });
    };

    private async addMuckpile() {
        const result = await this.dialog.openImportMuckpileDialog();
        if (result?.uploadType === "Manual") {
            const polygonCoords = await this.draw('Polygon');
            if (polygonCoords) {
                await this.dialog.open(this.addSiteDialog, polygonCoords as geojson.GeometryPolygon);
                this.mapInteractionDraw.removeDrawLayer(this.map)
            }
            this.trigger$.next();
        }
    }
    
    private toggleDeviceTooltips() {
        this.toggleTooltips('device');
    }

    private toggleMuckpileTooltips() {
        this.toggleTooltips('muckpile');
    }

    private toggleVehicleTooltips() {
        this.toggleTooltips('vehicle');
    }

    private toggleTooltips(type: TooltipType) {
        const isTooltipShown = this.tooltipVisibility[type];

        if (isTooltipShown) {
            this.tooltipManager.hideAllTooltips(type);
        } else {
            const features = this.getTooltipFeatures(type);
            if (features.length > 0) {
                this.tooltipManager.showTooltip(features);
            }
        }

        this.tooltipVisibility[type] = !isTooltipShown;
    }

    private refreshVisibleTooltips(type: TooltipType) {
        if (!this.tooltipVisibility[type]) {
            return;
        }

        this.tooltipManager.hideAllTooltips(type);

        const features = this.getTooltipFeatures(type);
        if (features.length > 0) {
            this.tooltipManager.showTooltip(features);
        }
    }

    private getTooltipFeatures(type: TooltipType) {
        if (type === 'muckpile') {
            return Array.from(this.polygonManager.polygonFeatures);
        }

        return Array.from(this.markerManager.features).filter((feature) => feature.type === type);
    }
    
    public async draw(
        drawingOption: DrawingManagerOption
    ): Promise<geojson.GeometryPolygon | geojson.GeometryPoint | null> {
        if (!this.map) {
            throw new Error('Map instance is not initialized.');
        }
        
        const drawInteraction = await this.mapInteractionDraw.addDrawInteractionAsync(this.map, drawingOption);
        
        if (drawingOption === 'Polygon') {
            return {
                type: 'Polygon',
                coordinates: drawInteraction.coordinates,
            } as geojson.GeometryPolygon;
        } else if (drawingOption === 'Point') {
            return {
                type: 'Point',
                coordinates: drawInteraction.coordinates
            } as geojson.GeometryPoint;
        }
        
        return null;
    }

    private startLiveVehicleTracing() {
        const liveVehicleSubscription = combineLatest([
            this.vehicleQuery.liveVehicle$,
            this.vehicleQuery.vehicles$,
            this.vehicleProperties$
        ]).pipe(
            map(([vehicles, selectedSiteVehicles, vehicleProperties]) => {
                const selectedVehicleIds = new Set(
                    (vehicleProperties?.length
                        ? vehicleProperties
                            .filter(([, , isSelected]) => isSelected)
                            .map(([vehicle]) => vehicle.id)
                        : (selectedSiteVehicles ?? []).map((vehicle) => vehicle.id))
                );

                return (vehicles ?? [])
                    .filter((vehicle) => selectedVehicleIds.has(vehicle.id))
                    .map(vehicle => {
                    const vehicleProp = vehicleProperties.find(prop => prop[0].id === vehicle.id);
                    return { ...vehicle, color: vehicleProp ? vehicleProp[1].toString() : '#DF0C0C', draggable: false };
                });
            })
        ).subscribe(vehiclesWithColor => {
            this.markerManager?.syncLiveMovableMarkers(vehiclesWithColor);
            this.refreshVisibleTooltips('vehicle');
        });
        this.subscriptions.add(liveVehicleSubscription);
    }
    
    public addMarker(props: {
        coordinates: geojson.Coord,
        coordinateSystem: 'utm' | 'wgs84',
        title: string,
        device: Device,
        editLocationEmitter?: EventEmitter<geojson.GeometryPoint>,
        iconPath?: string,
        color?: string,
        draggable?: boolean,
        type?: string
    }) {
        const content = `<div class="map-marker" style="padding: 5px; border-radius: 5px;">
                            ${props.title}
                        </div>`;
        const markerOptions:MarkerPoint = {
            coordinate: props.coordinates,
            coordinateSystem: props.coordinateSystem,
            content,
            StyleType: 'circle-icon',
            icon: props.iconPath,
            draggable: props.draggable ?? false,
            dropCallback: (geometry) =>
            this.changeDeviceLocation(geometry, props.device),
            category: 'device',
        };
    
        if (props.color) {
            markerOptions.color = props.color;
        }
    
        return this.markerManager.add(markerOptions);
    }

    public addPolygon(props: {
        coordinates: geojson.Coord[][],
        title: string;
        color: string,
    }) {
        const content = `<div class="map-marker" style="padding: 5px; border-radius: 5px;">
                            ${props.title}
                        </div>`;

        this.polygonManager.add({
            coordinates: props.coordinates,
            content,
            color: props.color
        })
    };

    public async changeDeviceLocation(geometry: geojson.GeometryPoint, device: Device) {
        if (!device) return;

        let saveDevice: Device;
        console.log(this.dialog, this.fragQuery)
        if (await this.dialog.confirm({ message: 'Are you sure you want to change the location of the device?' })) {
            saveDevice = {
                ...device,
                geometry: JSON.stringify(geometry)
            };
            await this.dialog.await(this.deviceClient.save(saveDevice));
        } else {
            this.vehicleQuery.deviceUuid$.next(device.uuid);
        }
        this.vehicleQuery.trigger$.next();
    }

    public cleanup() {
        if (this.map) {
            this.map.dispose();
            this.map = null;
        }
        this.mapButtons = null;
        this.markerManager = null;
        this.tooltipManager = null;
        this.polygonManager = null;
        this.polylineManager = null;
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}